import debounce from 'lodash/debounce';
import get from 'lodash/get';
import omit from 'lodash/omit';
import PropTypes from 'prop-types';
import React, {useCallback,useState} from 'react';
import { useNavigate } from 'react-router-dom';

import AddIcon from '@mui/icons-material/Add';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import FormControlLabel from '@mui/material/FormControlLabel';
import InputAdornment from '@mui/material/InputAdornment';
import LocationOnOutlined from "@mui/icons-material/LocationOnOutlined";
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import Switch from '@mui/material/Switch';
import Typography from "@mui/material/Typography";

import './SessionFilters.scss';
import BetaFeature from '../common/BetaFeature';
import CustomTextField from '../common/CustomTextField';
import DateRangeFilter from '../common/DateRangeFilter';
import FindPatientForm from '../patients/FindPatientForm';
import SelectPatientForm from '../scheduling/SelectPatientForm';
import config from "../../config/config";
import useIsMobile from "../../hooks/useIsMobile";
import { useGetCasesQuery } from '../../redux/services/caseApi';
import { formatPatientInitials } from '../../helpers/patients';
import { formatCaseName } from '../../helpers/cases';

const handleTextChange = ({onFilterChange}) => (e, {filters}) => {
  const text = get(e, 'target.value');
  if (!text) return onFilterChange(omit(filters, 'text'));
  return onFilterChange({...filters, text});
};

const handleLocationChange = ({onFilterChange}) => (e, {filters}) => {
  const location = get(e, 'target.value');
  if (!location) return onFilterChange(omit(filters, 'location'));
  return onFilterChange({...filters, location});
};

const sorts = {
  '-date': 'Newest First',
  'date': 'Oldest First',
};

export const SortButton = props => {
  const [anchorEl, setAnchorEl] = useState(null);
  const isMobile = useIsMobile();
  const handleClose = () => setAnchorEl(null);
  return (
    <Box>
      <Button
        id='sort-menu-btn'
        aria-controls={!!anchorEl ? 'sort-menu-btn' : undefined}
        aria-haspopup='true'
        aria-expanded={!!anchorEl ? 'true' : undefined}
        onClick={e => setAnchorEl(e.currentTarget)}
        variant='grayOutline'
        sx={{boxShadow: config().styles.defaultBoxShadow}}
      >
        <Stack direction="row" justifyContent={'space-between'} alignItems={'center'} spacing={1} style={{width: '100%'}}>
          <Typography variant='large500Tight' color={config().colors.gray}>
            {sorts[props.sort] || 'Sort'}
          </Typography>
          <SwapVertIcon sx={{fontSize: '1.5rem', color: 'var(--primary)'}}/>
        </Stack>
      </Button>
      <Menu
        id='sort-menu'
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'basic-button',
        }}
        disableScrollLock={true}
      >
        <MenuItem
          onClick={() => {
            props.onChange('-date');
            handleClose();
          }}
        >
          {sorts['-date']}
        </MenuItem>
        <MenuItem
          onClick={() => {
            props.onChange('date');
            handleClose();
          }}
        >
          {sorts['date']}
        </MenuItem>
      </Menu>
    </Box>
  );
};

const SessionFilters = ({filters, onFilterChange, isPatient}) => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [searchDialogIsOpen, setSearchDialogIsOpen] = useState(false);
  const debouncedLocationChangeHandler = useCallback(
    debounce(handleLocationChange({onFilterChange}), 500),
    [handleLocationChange]
  );
  const caseQuery = useGetCasesQuery(
    {id: filters?.case_id},
    {skip: !filters?.case_id}
  );
  const specificCase = caseQuery?.data?.data?.[0];
  return (
    <>
      <Dialog
        fullWidth={true}
        maxWidth='sm'
        open={searchDialogIsOpen}
        onClose={() => setSearchDialogIsOpen(false)}
      >
        <SelectPatientForm title='Find a Patient' />
      </Dialog>
      <Stack direction='row' spacing={{xs: 1.25, md: 3.65}} className="session-filter session-filter__container">
        <Stack className='w-100' direction='row' justifyContent='center' spacing={1}>
          {
            isPatient ? null :
              <FindPatientForm
                sx={{...inputStyle}}
                rounded={true}
                defaultValue={filters?.patient_id}
                fullWidth={true}
                allPatients={false}
                onSubmit={({patientId}) => {
                  let _filters = (
                    patientId
                    ? {...filters, patient_id: patientId}
                    : omit(filters, 'patient_id', 'include_nonowned_sessions')
                  );
                  onFilterChange(_filters);
                }}
            />
          }
          {
            filters.patient_id
            ? <FormControlLabel
              control={
                <Switch
                  defaultChecked
                  onChange={e => onFilterChange({...filters, include_nonowned_sessions: !e.target.checked})}
                />
              }
              label="Only my sessions"
            />
            : null
          }
        </Stack>
        {
          isPatient ? null :
            <Box className='w-100'>
              <CustomTextField
                placeholder={isMobile ? 'Location' : 'Search Location'}
                sx={{...inputStyle}}
                size='small'
                fullWidth={true}
                rounded={true}
                onChange={e => debouncedLocationChangeHandler(e, {filters})}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LocationOnOutlined style={{color: config().colors.primary, fontSize: '1.25rem', marginLeft: '4px'}} />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

        }
        {
            filters?.case_id && specificCase
            ? <Chip 
              color='secondary'
              label={formatCaseName(specificCase)}
              avatar={
                <Avatar>
                  {formatPatientInitials(specificCase?.patient)}
                </Avatar>
              }
              onDelete={() => onFilterChange(omit(filters, 'case_id'))}
            />
            : null
          }
        {/* {
          <BetaFeature>
            <Button 
              color={filters.bookmarked ? 'secondary' : 'primary'}
              onClick={e => onFilterChange({...filters, bookmarked: !filters.bookmarked})}
            >
              { filters.bookmarked
                ? <BookmarkIcon />
                : <BookmarkBorderIcon/>
              }
            </Button>
          </BetaFeature>
        } */}
        {
          <div>
            <Button 
              variant='primaryGradient'
              size='md'
              onClick={() => 
                isPatient
                ? navigate('/clients/browse-providers')
                : setSearchDialogIsOpen(true)
              }
            >
              <AddIcon />
              Schedule
            </Button>
          </div>
        }
        {
            <DateRangeFilter 
              startDate={filters.start_time}
              endDate={filters.end_time}
              iconOnly
              isActive={filters.start_time || filters.end_time}
              onSubmit={(e, {startDate, endDate}) => {
                let newFilters = omit(filters, 'start_time', 'end_time');
                if (startDate) newFilters.start_time = startDate;
                if (endDate) newFilters.end_time = endDate;
                onFilterChange(newFilters);
              }}
            />
        }
        <div className="session-filter__sort">
          <SortButton
            sort={filters.sort}
            onChange={sort => onFilterChange({...filters, sort})}
          />
        </div>
      </Stack>
    </>
  );
};

const inputStyle = {
  boxShadow: config().styles.defaultBoxShadow,
  '.MuiInputBase-root': {
    height: '38px'
  }
}

SessionFilters.propTypes = {
  filters: PropTypes.object,
  onFilterChange: PropTypes.func,
};

export default SessionFilters;
